import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    CheckCircleOutline,
    HighlightOff,
    TipsAndUpdates,
    EmojiPeople,
    Group,
    WarningAmber,
    ThumbUpAlt,
    Info,
    Star
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', fontSize: '1.1rem', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                                    1.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('introduction')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    ルームシェアの適性診断とは？
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', fontSize: '1.1rem', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                                    2.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('positive-personality')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    ルームシェアに向いている人の特徴
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', fontSize: '1.1rem', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                                    3.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('negative-personality')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    ルームシェアに向いていない人の傾向と注意点
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', fontSize: '1.1rem', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                                    4.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('tips')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    適性を高めるコツ・トレーニング
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', fontSize: '1.1rem', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                                    5.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('conclusion')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    まとめ・自分に合った選択を
                                </Link>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入部分 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアの適性診断とは？
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、<b>ルームシェア</b>は若者や社会人、シニア世代まで幅広い層に人気の住まい方になっています。家賃や生活費の節約、人とのつながり、刺激的な毎日——その一方で「思っていたより大変だった」「性格的に合わなかった」という声もちらほら。
                </Typography>
                <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                    <img
                        src="/images/blog/living-dining_roomshare-seikaku.jpg"
                        alt="明るい共有リビングで複数のルームメイトが談笑しながら朝食をとる様子。木目調のテーブルや観葉植物、柔らかな照明で温かな雰囲気が漂う空間"
                        style={{ width: '100%', maxWidth: 540, borderRadius: 12, boxShadow: '0 2px 8px #bbb' }}
                    />
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    この記事では、<b>どんな性格がルームシェアに向いているのか</b>、逆に「こんなタイプはちょっと注意！」という傾向、そして適性を高めるための具体的なコツまで、専門家の視点からわかりやすく解説します。
                </Typography>
                <Typography variant="body2" sx={{ color: '#1976d2', mb: 2 }}>
                    「自分はルームシェアに向いているのか？」<br />
                    本記事でセルフチェックしてみましょう！
                </Typography>
            </Box>

            {/* ルームシェアに向いている人の特徴 */}
            <Box id="positive-personality" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアに向いている人の特徴
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアで楽しく快適に暮らせる人には、いくつか共通する性格や行動パターンがあります。あなたはいくつ当てはまるでしょうか？
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <CheckCircleOutline color="primary" />
                                </ListItemIcon>
                                <Typography variant="h6">コミュニケーション力がある</Typography>
                            </Box>
                            <Typography variant="body2">
                                些細なことでもきちんと伝え合える人は、トラブルを未然に防ぎやすいです。<b>相手の立場を考え、柔軟に話し合えること</b>が大切。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <Group color="primary" />
                                </ListItemIcon>
                                <Typography variant="h6">協調性・思いやりがある</Typography>
                            </Box>
                            <Typography variant="body2">
                                ルームメイトの多様な価値観や生活リズムを尊重できる人は、円滑な共同生活に向いています。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <ThumbUpAlt color="primary" />
                                </ListItemIcon>
                                <Typography variant="h6">ある程度おおらか</Typography>
                            </Box>
                            <Typography variant="body2">
                                細かいことにこだわりすぎず、<b>小さな違いを楽しめる心の余裕</b>がある人はストレスが溜まりにくいです。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <EmojiPeople color="primary" />
                                </ListItemIcon>
                                <Typography variant="h6">柔軟な考え方・新しい体験好き</Typography>
                            </Box>
                            <Typography variant="body2">
                                変化や新たな人との交流をポジティブに受け入れられる人は、ルームシェアの「刺激」を楽しめます。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/share-house-kitchen_roomshare-seikaku.jpg"
                        alt="広々としたキッチンで複数のルームメイトが協力して料理をしている様子。明るく清潔感のある空間で、協調性やチームワークが感じられる"
                        style={{ width: '100%', maxWidth: 540, borderRadius: 12, boxShadow: '0 2px 8px #bbb' }}
                    />
                </Box>
                <Typography variant="body1" sx={{ mt: 2 }}>
                    さらに、<b>自分のルールや価値観を押し付けすぎない</b>、<b>生活のマナーや基本的な清潔感がある</b>なども重要なポイントです。
                </Typography>
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <Star color="primary" sx={{ mr: 1, verticalAlign: 'bottom' }} />
                        こんな人は特にルームシェアに向いている！
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="人と一緒に過ごすのが好き" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="新しい文化や価値観に興味がある" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="生活リズムの調整や妥協ができる" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="自分から声をかけたり、困った時は助けを求められる" />
                        </ListItem>
                    </List>
                </Box>
            </Box>

            {/* ルームシェアに向いていない人の傾向と注意点 */}
            <Box id="negative-personality" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアに向いていない人の傾向と注意点
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    どんなに人気のルームシェアでも、性格によってはストレスを感じやすい場合も…。向いていない傾向がある人の特徴と、トラブルを防ぐための注意点を紹介します。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <HighlightOff color="error" />
                                </ListItemIcon>
                                <Typography variant="h6">自分のペースやスペースを絶対守りたい</Typography>
                            </Box>
                            <Typography variant="body2">
                                プライベート空間を重視しすぎる人は、<b>共同部分の使い方や生活音</b>にストレスを感じやすいです。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <WarningAmber color="error" />
                                </ListItemIcon>
                                <Typography variant="h6">こだわりや潔癖が強い</Typography>
                            </Box>
                            <Typography variant="body2">
                                <b>清掃や整理整頓</b>、生活リズム、音や匂いに「こうでなければダメ！」という強いこだわりがあると、他人との暮らしは疲れることも。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <HighlightOff color="error" />
                                </ListItemIcon>
                                <Typography variant="h6">自己主張が強すぎる・我慢できない</Typography>
                            </Box>
                            <Typography variant="body2">
                                些細なことでイライラしたり、<b>自分の意見を押し通したい</b>タイプは、衝突が起こりやすくなります。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <WarningAmber color="error" />
                                </ListItemIcon>
                                <Typography variant="h6">人との関わりが苦手・一人が好き</Typography>
                            </Box>
                            <Typography variant="body2">
                                <b>人付き合いが苦手</b>だったり、<b>孤独を好む</b>人は、共同生活の楽しさより負担が勝りやすいです。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/private-room_roomshare-seikaku.jpg"
                        alt="個室のベッドに座り、物思いにふけるルームメイトの姿。落ち着いた色合いのインテリアで、静かなプライベート空間が強調されている"
                        style={{ width: '100%', maxWidth: 540, borderRadius: 12, boxShadow: '0 2px 8px #bbb' }}
                    />
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <WarningAmber color="warning" sx={{ mr: 1, verticalAlign: 'bottom' }} />
                        注意すべきポイント
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><HighlightOff color="error" /></ListItemIcon>
                            <ListItemText primary="自分のテリトリーを他人に侵されたくない" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><HighlightOff color="error" /></ListItemIcon>
                            <ListItemText primary="他人の生活音や行動に敏感でストレスを感じやすい" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><HighlightOff color="error" /></ListItemIcon>
                            <ListItemText primary="対話や妥協より自分の正しさを優先しがち" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><HighlightOff color="error" /></ListItemIcon>
                            <ListItemText primary="他人に合わせるのが苦痛" />
                        </ListItem>
                    </List>
                </Box>
                <Typography variant="body2" sx={{ mt: 2, color: '#d32f2f' }}>
                    ただし、「向いていない」と感じた人でも、<b>事前の準備や意識の持ち方次第で快適なルームシェアは十分可能</b>です！
                </Typography>
            </Box>

            {/* 適性を高めるコツ・トレーニング */}
            <Box id="tips" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    適性を高めるコツ・トレーニング
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「ちょっと不安…」という方も、<b>ちょっとしたコツや意識</b>でルームシェア適性は格段にアップします。実践的なポイントを紹介します。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <TipsAndUpdates color="primary" />
                                </ListItemIcon>
                                <Typography variant="h6">小さなことでも「ありがとう」を伝える</Typography>
                            </Box>
                            <Typography variant="body2">
                                感謝の気持ちを素直に言葉にするだけで、<b>人間関係がぐっと良くなります</b>。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <Info color="primary" />
                                </ListItemIcon>
                                <Typography variant="h6">約束事・ルールを明文化する</Typography>
                            </Box>
                            <Typography variant="body2">
                                <b>ゴミ出しや掃除の分担、共有スペースの使い方</b>など、最初にしっかり話し合って決めましょう。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <TipsAndUpdates color="primary" />
                                </ListItemIcon>
                                <Typography variant="h6">自分の「苦手」を伝える勇気</Typography>
                            </Box>
                            <Typography variant="body2">
                                我慢しすぎず、「これだけは苦手」と<em>正直に伝える</em>ことでお互いに配慮しやすくなります。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ListItemIcon>
                                    <TipsAndUpdates color="primary" />
                                </ListItemIcon>
                                <Typography variant="h6">自分の時間も大切に</Typography>
                            </Box>
                            <Typography variant="body2">
                                <b>無理に全員と仲良くしようとせず</b>、自分らしい過ごし方も尊重しましょう。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>
                    
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <TipsAndUpdates color="primary" sx={{ mr: 1, verticalAlign: 'bottom' }} />
                        専門家からのアドバイス
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><TipsAndUpdates color="primary" /></ListItemIcon>
                            <ListItemText primary="小さな違和感もため込まず、早めに伝えること" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><TipsAndUpdates color="primary" /></ListItemIcon>
                            <ListItemText primary="「できること」「できないこと」の線引きを自分の中で決めておく" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><TipsAndUpdates color="primary" /></ListItemIcon>
                            <ListItemText primary="自分のプライベートな趣味や時間を持つ" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><TipsAndUpdates color="primary" /></ListItemIcon>
                            <ListItemText primary="家族や友人のような距離感を持とうとせず、適度な距離を意識する" />
                        </ListItem>
                    </List>
                </Box>
            </Box>

            {/* まとめ・自分に合った選択を */}
            <Box id="conclusion" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ・自分に合った選択を
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアには「向いている性格」も「向いていない傾向」も確かにありますが、<b>大切なのは自分の強みや苦手を理解し、納得した上で選ぶこと</b>です。
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    もし不安があれば、<b>短期間のお試しシェア</b>や、<b>個室重視の物件</b>からスタートするのもおすすめ。自分に合ったスタイルを探すことで、ルームシェアの良さを最大限に味わえるでしょう。
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Chip label="コミュニケーション" color="primary" variant="outlined" />
                    <Chip label="協調性" color="primary" variant="outlined" />
                    <Chip label="適性診断" color="primary" variant="outlined" />
                    <Chip label="快適な暮らし" color="primary" variant="outlined" />
                    <Chip label="ライフスタイル" color="secondary" variant="outlined" />
                </Box>
                <Box sx={{ mt: 4 }}>
                    <Paper elevation={3} sx={{ p: 3, borderRadius: 2, bgcolor: '#fafafa', textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            <Info color="primary" sx={{ mr: 1, verticalAlign: 'bottom' }} />
                            ルームシェア適性は「変えられる」！
                        </Typography>
                        <Typography variant="body2">
                            向き・不向きは「絶対」ではありません。<br />
                            少しの工夫やトレーニングで、<b>あなたの適性も大きく伸ばすことができます</b>。<br />
                            ぜひ自分らしいルームシェアライフを見つけてください。
                        </Typography>
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-seikaku',
    slug: 'roomshare-seikaku',
    title: 'ルームシェアに向いている性格・向いていない性格とは？',
    excerpt: 'ルームシェアの適性診断。向いている人の特徴と注意すべき性格傾向、適性を高めるコツまで専門的に解説します。自分に合ったルームシェア生活を見つけるヒントが満載。',
    coverImage: '/images/blog/cover-roomshare-seikaku_roomshare-seikaku.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木目調の家具と観葉植物、リラックスした雰囲気が印象的で、共同生活の温かさと活気が感じられる',
    publishedAt: '2024-06-16T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', 'ライフスタイル', '心理・適性'],
    tags: ['ルームシェア', '性格診断', '適性', 'コミュニケーション', '共同生活'],
    author: {
        name: '山本 直樹',
        avatar: '/images/avatars/yamamoto-naoki.jpg',
        bio: 'ルームシェア・シェアハウス事情に詳しい住まいと人間関係のコンサルタント。全国のシェア物件や入居者サポートの実績多数。快適な共同生活のノウハウを分かりやすく発信中。',
        profilePicture: '/images/avatars/yamamoto-naoki.jpg'
    },
    viewCount: 1630,
    contentComponent: PostContent
};

export default post;